import { useState, useRef } from 'react';
import { ArrowLeftIcon, PhotoIcon, TrashIcon, CheckIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { Link, useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';

import { Card, Button, Spinner, Input, Badge } from '@realvisor/ui';
import { useProject, useUpdateProject, usePipelines } from '@/hooks';
import { api } from '@/lib/api';

const PROJECT_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#8B5CF6', // purple
  '#F59E0B', // amber
  '#EF4444', // red
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#6366F1', // indigo
  '#F97316', // orange
];

export default function ProjectSettings() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading, error, refetch } = useProject(id || '');
  const { data: pipelines } = usePipelines();
  const updateProject = useUpdateProject();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editPipelineId, setEditPipelineId] = useState('');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form when project loads
  useState(() => {
    if (project) {
      setEditName(project.name);
      setEditDescription(project.description || '');
      setEditColor(project.color || '#3B82F6');
      setEditPipelineId(project.defaultPipelineId || '');
    }
  });

  // Update form when project changes
  if (project && !editName) {
    setEditName(project.name);
    setEditDescription(project.description || '');
    setEditColor(project.color || '#3B82F6');
    setEditPipelineId(project.defaultPipelineId || '');
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !project) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Povolené formáty: JPEG, PNG, WebP, SVG');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Maximální velikost souboru je 2MB');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.uploadFile(`/projects/${project.id}/logo`, formData);
      toast.success('Logo nahráno');
      refetch();
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    } catch (error) {
      toast.error('Nepodařilo se nahrát logo');
    } finally {
      setIsUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!project) return;
    
    try {
      await api.delete(`/projects/${project.id}/logo`);
      toast.success('Logo odstraněno');
      refetch();
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    } catch (error) {
      toast.error('Nepodařilo se odstranit logo');
    }
  };

  const handleSave = async () => {
    if (!project) return;
    
    try {
      await updateProject.mutateAsync({
        id: project.id,
        data: {
          name: editName,
          description: editDescription || undefined,
          color: editColor,
          defaultPipelineId: editPipelineId || undefined,
        },
      });
      toast.success('Nastavení uloženo');
      setHasChanges(false);
    } catch (error) {
      toast.error('Nepodařilo se uložit nastavení');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <p className="text-red-500">Projekt nebyl nalezen</p>
          <Link to="/projects" className="mt-4 inline-block text-primary-600 hover:underline">
            Zpět na seznam
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={`/projects/${project.id}`}
            className="flex items-center justify-center rounded-lg border border-gray-200 p-2 hover:bg-gray-50"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Nastavení projektu</h1>
            <p className="text-sm text-gray-500">{project.name}</p>
          </div>
        </div>
        <Button 
          onClick={handleSave}
          disabled={!editName.trim() || updateProject.isPending}
        >
          {updateProject.isPending ? 'Ukládám...' : 'Uložit změny'}
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Základní informace */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Základní informace</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Název projektu *
                  </label>
                  <Input
                    value={editName}
                    onChange={(e) => {
                      setEditName(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="Název projektu"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Popis
                  </label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => {
                      setEditDescription(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="Volitelný popis projektu"
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug
                  </label>
                  <Input
                    value={project.slug}
                    disabled
                    className="bg-gray-50"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Slug nelze změnit po vytvoření projektu
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Pipeline */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Pipeline</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Výchozí pipeline
                </label>
                <select
                  value={editPipelineId}
                  onChange={(e) => {
                    setEditPipelineId(e.target.value);
                    setHasChanges(true);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Žádná (použít systémovou)</option>
                  {pipelines?.map((pipeline) => (
                    <option key={pipeline.id} value={pipeline.id}>
                      {pipeline.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Pipeline určuje fáze, kterými procházejí leady v tomto projektu
                </p>
              </div>
            </div>
          </Card>

          {/* Členové projektu */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Členové projektu</h2>
                <Button variant="outline" size="sm" disabled>
                  <UserPlusIcon className="h-4 w-4 mr-1" />
                  Přidat člena
                </Button>
              </div>
              <div className="text-center py-8 text-gray-500">
                <p>Správa členů projektu bude brzy dostupná</p>
                <p className="text-sm mt-1">
                  Zde budete moci přidávat uživatele a nastavovat jejich role v projektu
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Branding */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Branding</h2>
              
              {/* Logo */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo projektu
                </label>
                <div className="flex items-center gap-4">
                  {project.logoUrl && project.logoUrl.startsWith('http') ? (
                    <div className="relative">
                      <img 
                        src={project.logoUrl} 
                        alt="Logo projektu" 
                        className="h-20 w-20 rounded-lg object-contain border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={handleDeleteLogo}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-20 w-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                      <PhotoIcon className="h-10 w-10 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/svg+xml"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingLogo}
                    >
                      {isUploadingLogo ? 'Nahrávám...' : (project.logoUrl && project.logoUrl.startsWith('http')) ? 'Změnit' : 'Nahrát'}
                    </Button>
                    <p className="mt-1 text-xs text-gray-500">
                      Max 2MB
                    </p>
                  </div>
                </div>
              </div>

              {/* Barva */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Barva projektu
                </label>
                <div className="flex flex-wrap gap-2">
                  {PROJECT_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => {
                        setEditColor(color);
                        setHasChanges(true);
                      }}
                      className={`h-8 w-8 rounded-full border-2 flex items-center justify-center transition-all ${
                        editColor === color 
                          ? 'border-gray-900 scale-110' 
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    >
                      {editColor === color && (
                        <CheckIcon className="h-4 w-4 text-white" />
                      )}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Barva se používá pro vizuální odlišení projektu v seznamech
                </p>
              </div>
            </div>
          </Card>

          {/* Info */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Informace</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Vytvořeno</span>
                  <span className="font-medium">
                    {new Date(project.createdAt).toLocaleDateString('cs-CZ')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Stav</span>
                  <Badge variant={project.archivedAt ? 'default' : 'success'}>
                    {project.archivedAt ? 'Archivován' : 'Aktivní'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Počet leadů</span>
                  <span className="font-medium">{project._count?.leads || 0}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Danger Zone */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-red-600 mb-4">Nebezpečná zóna</h2>
              <p className="text-sm text-gray-500 mb-4">
                Archivace projektu skryje projekt ze seznamu, ale zachová všechna data.
              </p>
              <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" disabled>
                Archivovat projekt
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
